<template>
  <div
    class="mobile-book-controls"
    :class="{ 'mobile-book-controls--landscape': isLandscape }"
    @click.stop
    @dblclick.stop
    @pointerdown="onControlPointerDown"
    @pointermove.stop
    @pointerup="onControlPointerEnd"
    @pointercancel="onControlPointerEnd"
    @mousedown.stop
    @mousemove.stop
    @mouseup.stop
    @touchstart.stop.passive
    @touchmove.stop.passive
    @touchend.stop
    @touchcancel.stop
    @keydown.stop
    @keyup.stop
    @input.stop
    @change.stop
    @contextmenu.stop.prevent
    @dragstart.stop.prevent
    @wheel.stop.prevent
  >
    <button
      type="button"
      class="mobile-book-controls__round-button mobile-book-controls__previous"
      aria-label="Previous page"
      :disabled="!canGoPrev"
      :style="prevButtonStyle"
      @click="emit('prev')"
    >
      <BookControlIcon :name="BOOK_ICON_NAMES.BOOK_PREVIOUS" :size="28" />
    </button>

    <div class="mobile-book-controls__primary-actions">
      <button
        type="button"
        class="mobile-book-controls__round-button mobile-book-controls__settings"
        aria-label="Book tools"
        :aria-expanded="toolsOpen"
        @click="toolsOpen = !toolsOpen"
      >
        <img :src="settingsIcon" alt="" aria-hidden="true" draggable="false" />
      </button>

      <button
        type="button"
        class="mobile-book-controls__round-button mobile-book-controls__play"
        :class="{ 'is-active': isPlaying }"
        :style="playProgressStyle"
        :aria-label="isPlaying ? 'Pause autoplay' : 'Start autoplay'"
        :aria-pressed="isPlaying"
        @click="emit('togglePlay')"
      >
        <BookControlIcon
          :name="isPlaying ? BOOK_ICON_NAMES.BOOK_PAUSE : BOOK_ICON_NAMES.BOOK_PLAY"
        />
      </button>
    </div>

    <Transition name="mobile-book-tools">
      <div v-if="toolsOpen" class="mobile-book-controls__tools" aria-label="Book tools">
        <div class="mobile-book-controls__toolbar">
          <button
            type="button"
            class="mobile-book-controls__tool-button"
            aria-label="Adjust volume"
            :aria-expanded="volumeOpen"
            @click="toggleVolumePanel"
          >
            <BookControlIcon
              :name="
                effectiveVolume === 0 ? BOOK_ICON_NAMES.BOOK_SOUND_OFF : BOOK_ICON_NAMES.BOOK_SOUND
              "
            />
          </button>

          <!-- 无背景音乐时不渲染 BGM 按钮 -->
          <button
            v-if="hasBgm"
            type="button"
            class="mobile-book-controls__tool-button"
            :aria-label="isBgmPlaying ? 'Pause background music' : 'Play background music'"
            :aria-pressed="isBgmPlaying"
            :aria-busy="!isBgmReady"
            :disabled="!canToggleBgm"
            @click="emit('toggleBgm')"
          >
            <img
              :src="isBgmPlaying ? bgmIcon : bgmOffIcon"
              alt=""
              aria-hidden="true"
              draggable="false"
            />
          </button>

          <span class="mobile-book-controls__divider" aria-hidden="true"></span>

          <button
            type="button"
            class="mobile-book-controls__tool-button mobile-book-controls__tool-button--compact"
            :class="{ 'is-active': isZoomed }"
            :aria-label="isZoomed ? 'Exit zoom' : 'Zoom in'"
            :aria-pressed="isZoomed"
            @click="emit('toggleZoom')"
          >
            <BookControlIcon
              :name="isZoomed ? BOOK_ICON_NAMES.BOOK_ZOOM_RESTORE : BOOK_ICON_NAMES.BOOK_ZOOM"
            />
          </button>

          <button
            v-if="fullscreenSupported"
            type="button"
            class="mobile-book-controls__tool-button"
            :class="{ 'is-active': isFullscreen }"
            :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
            :aria-pressed="isFullscreen"
            @click="emit('toggleFullscreen')"
          >
            <BookControlIcon
              :name="
                isFullscreen
                  ? BOOK_ICON_NAMES.BOOK_FULLSCREEN_EXIT
                  : BOOK_ICON_NAMES.BOOK_FULLSCREEN
              "
            />
          </button>

          <button
            type="button"
            class="mobile-book-controls__layout-button"
            aria-label="选择按钮布局"
            :aria-expanded="layoutPickerOpen"
            aria-controls="mobile-book-layout-picker"
            @click="toggleLayoutPicker"
          >
            <span class="mobile-book-controls__layout-icon" aria-hidden="true">
              <span></span>
            </span>
          </button>

          <button
            type="button"
            class="mobile-book-controls__tool-button"
            aria-label="Share"
            @click="emit('share')"
          >
            <BookControlIcon :name="BOOK_ICON_NAMES.BOOK_SHARE" />
          </button>
        </div>

        <Transition name="mobile-layout-picker">
          <div
            v-if="layoutPickerOpen"
            id="mobile-book-layout-picker"
            class="mobile-book-controls__layout-picker"
            aria-label="选择按钮布局"
          >
            <div class="mobile-book-controls__layout-picker-heading">选择按钮布局</div>
            <div class="mobile-book-controls__layout-options">
              <button
                type="button"
                class="mobile-book-controls__layout-option"
                :class="{ 'is-selected': excludeControls }"
                :aria-pressed="excludeControls"
                @click="emit('update:excludeControls', true)"
              >
                <span
                  class="mobile-book-controls__layout-preview mobile-book-controls__layout-preview--excluded"
                  aria-hidden="true"
                >
                  <span class="mobile-book-controls__layout-preview-content"></span>
                  <span class="mobile-book-controls__layout-preview-rail"></span>
                </span>
                <span class="mobile-book-controls__layout-copy">
                  <span class="mobile-book-controls__layout-title">不遮挡布局</span>
                  <span class="mobile-book-controls__layout-description">
                    按钮在独立区域，内容完整显示
                  </span>
                </span>
              </button>

              <button
                type="button"
                class="mobile-book-controls__layout-option"
                :class="{ 'is-selected': !excludeControls }"
                :aria-pressed="!excludeControls"
                @click="emit('update:excludeControls', false)"
              >
                <span
                  class="mobile-book-controls__layout-preview mobile-book-controls__layout-preview--overlay"
                  aria-hidden="true"
                >
                  <span class="mobile-book-controls__layout-preview-content"></span>
                  <span class="mobile-book-controls__layout-preview-control"></span>
                </span>
                <span class="mobile-book-controls__layout-copy">
                  <span class="mobile-book-controls__layout-title">遮挡布局</span>
                  <span class="mobile-book-controls__layout-description">
                    按钮浮于画面，点击屏幕关闭
                  </span>
                </span>
              </button>
            </div>
          </div>
        </Transition>

        <Transition name="mobile-volume">
          <div v-if="volumeOpen" class="mobile-book-controls__volume" aria-label="Volume">
            <button
              type="button"
              class="mobile-book-controls__volume-mute"
              :aria-label="isMuted ? 'Unmute' : 'Mute'"
              :aria-pressed="isMuted"
              @click="emit('toggleSound')"
            >
              <BookControlIcon
                :name="
                  effectiveVolume === 0
                    ? BOOK_ICON_NAMES.BOOK_SOUND_OFF
                    : BOOK_ICON_NAMES.BOOK_SOUND
                "
              />
            </button>
            <input
              ref="volumeRangeRef"
              class="mobile-book-controls__volume-range"
              type="range"
              min="0"
              max="1"
              step="0.01"
              :value="effectiveVolume"
              :style="volumeRangeStyle"
              aria-label="Volume"
              @input="onVolumeInput"
              @pointerdown="onVolumePointerDown"
              @pointermove="onVolumePointerMove"
              @pointerup="onVolumePointerUp"
              @pointercancel="onVolumePointerUp"
            />
            <span class="mobile-book-controls__volume-value">{{ volumePercentLabel }}</span>
          </div>
        </Transition>
      </div>
    </Transition>

    <div
      class="mobile-book-controls__page"
      :class="{ 'mobile-book-controls__page--bottom': isBottomLayout }"
      :style="pageLabelStyle"
      :aria-label="pageStatusLabel"
    >
      {{ currentPageLabel }}/{{ totalPages }}
    </div>

    <button
      type="button"
      class="mobile-book-controls__round-button mobile-book-controls__next"
      aria-label="Next page"
      :disabled="!canGoNext"
      :style="nextButtonStyle"
      @click="emit('next')"
    >
      <BookControlIcon :name="BOOK_ICON_NAMES.BOOK_NEXT" :size="28" />
    </button>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch, type CSSProperties } from 'vue';
  import { storeToRefs } from 'pinia';
  import bgmIcon from '@/assets/icons/svg/book/mobile-bgm.svg?url';
  import bgmOffIcon from '@/assets/icons/svg/book/mobile-bgm-off.svg?url';
  import settingsIcon from '@/assets/icons/svg/book/mobile-settings.svg?url';
  import { EVENT_NAMES } from '@/constants';
  import { BOOK_ICON_NAMES } from '@/constants/svg';
  import { emitter } from '@/utils/emitter';
  import { isNativeFullscreenSupported } from '@/utils/fullscreen';
  import { useReaderControlsStore } from '@/store/readerControls';
  import BookControlIcon from './BookControlIcon.vue';

  /** 书边到按钮外缘的间隙 */
  const SIDE_GAP_PX = 20;
  /** 圆形翻页按钮边长 */
  const SIDE_BUTTON_SIZE_PX = 40;
  /** 留白不足时回退的侧轨宽度（按钮 + 间隙） */
  const SIDE_RAIL_WIDTH_PX = SIDE_BUTTON_SIZE_PX + SIDE_GAP_PX;
  /** 回退时按钮相对视口边缘的偏移（侧轨居中） */
  const FALLBACK_SIDE_OFFSET_PX = (SIDE_RAIL_WIDTH_PX - SIDE_BUTTON_SIZE_PX) / 2;
  /** 竖屏单页底部布局：按钮/页码到书本底边的间隙 */
  const BOOK_BOTTOM_GAP_PX = 20;
  /** 竖屏单页底部布局：越出视口时距视口底部保留的最小间距 */
  const BOTTOM_SAFE_MARGIN_PX = 8;
  /** 竖屏单页底部布局：按钮外缘到页码中心的水平距离（按钮 40 + 间距 24 + 页码半宽 36） */
  const BOTTOM_BUTTON_CENTER_OFFSET_PX = 100;

  const props = defineProps<{
    isLandscape: boolean;
    excludeControls: boolean;
  }>();

  const {
    currentPageLabel,
    totalPages,
    displayMode,
    canGoPrev,
    canGoNext,
    muted: isMuted,
    isPlaying,
    isZoomed,
    isFullscreen,
    playbackProgress,
    effectiveVolume,
    hasBgm,
    isBgmPlaying,
    isBgmReady,
    canToggleBgm
  } = storeToRefs(useReaderControlsStore());

  const emit = defineEmits<{
    prev: [];
    next: [];
    share: [];
    toggleBgm: [];
    toggleZoom: [];
    toggleFullscreen: [];
    togglePlay: [];
    toggleSound: [];
    setVolume: [value: number];
    'update:excludeControls': [enabled: boolean];
    interactionChange: [active: boolean];
  }>();

  /** 设备是否支持原生全屏；不支持时不渲染全屏按钮（挂载后不会变化，无需响应式） */
  const fullscreenSupported = isNativeFullscreenSupported();

  const toolsOpen = ref(false);
  const volumeOpen = ref(false);
  const layoutPickerOpen = ref(false);
  const volumeRangeRef = ref<HTMLInputElement | null>(null);
  const activeControlPointers = new Set<number>();
  let volumeDragging = false;
  let interactionActive = false;

  /** 上一页/下一页相对容器的定位（默认回退到两侧 60px 轨居中） */
  const prevButtonStyle = ref<CSSProperties>({
    top: `calc(50% - ${SIDE_BUTTON_SIZE_PX / 2}px)`,
    left: `${FALLBACK_SIDE_OFFSET_PX}px`
  });
  const nextButtonStyle = ref<CSSProperties>({
    top: `calc(50% - ${SIDE_BUTTON_SIZE_PX / 2}px)`,
    right: `${FALLBACK_SIDE_OFFSET_PX}px`
  });
  /** 底部布局时页码的定位覆盖；侧栏布局时为 undefined，走 CSS 默认定位 */
  const pageLabelStyle = ref<CSSProperties | undefined>(undefined);

  /** 书本显示矩形载荷（容器 CSS 坐标） */
  type BookDisplayRect = {
    left: number;
    top: number;
    width: number;
    height: number;
    containerWidth: number;
    containerHeight: number;
  };

  /** 最近一次书本显示矩形，供布局模式切换时重算位置 */
  let lastBookRect: BookDisplayRect | null = null;

  /** 竖屏 + 单页模式 → 按钮与页码走书本底部布局 */
  const isBottomLayout = computed(() => !props.isLandscape && displayMode.value === 'single');

  /**
   * 根据书本显示矩形计算翻页按钮与页码位置。
   * 步骤：
   * 1. 竖屏单页 → 底部布局：一排放在书底下方 20px，用 min() 防止越出视口底部
   * 2. 其他 → 侧栏布局：左右留白均 ≥ 60px 时贴书 20px，否则回退 60px 轨居中
   */
  const applyControlsLayout = () => {
    if (!lastBookRect) return;
    const { left, top, width, height, containerWidth } = lastBookRect;

    // 步骤1：竖屏单页底部布局
    if (isBottomLayout.value) {
      // 书底 + 20px；越出视口时收回到视口底部安全边距内
      const buttonTop = `min(${Math.round(top + height + BOOK_BOTTOM_GAP_PX)}px, calc(100% - ${SIDE_BUTTON_SIZE_PX}px - max(${BOTTOM_SAFE_MARGIN_PX}px, env(safe-area-inset-bottom))))`;
      prevButtonStyle.value = {
        top: buttonTop,
        left: `calc(50% - ${BOTTOM_BUTTON_CENTER_OFFSET_PX}px)`
      };
      nextButtonStyle.value = {
        top: buttonTop,
        right: `calc(50% - ${BOTTOM_BUTTON_CENTER_OFFSET_PX}px)`
      };
      pageLabelStyle.value = { top: buttonTop };
      return;
    }

    // 步骤2：侧栏布局（横屏或双页）
    pageLabelStyle.value = undefined;
    const rightMargin = containerWidth - left - width;
    const canStickToBook = left >= SIDE_RAIL_WIDTH_PX && rightMargin >= SIDE_RAIL_WIDTH_PX;
    const buttonTop = Math.round(top + height / 2 - SIDE_BUTTON_SIZE_PX / 2);
    const prevLeft = canStickToBook
      ? Math.round(left - SIDE_GAP_PX - SIDE_BUTTON_SIZE_PX)
      : FALLBACK_SIDE_OFFSET_PX;
    const nextRight = canStickToBook
      ? Math.round(rightMargin - SIDE_GAP_PX - SIDE_BUTTON_SIZE_PX)
      : FALLBACK_SIDE_OFFSET_PX;

    prevButtonStyle.value = {
      top: `${buttonTop}px`,
      left: `${prevLeft}px`
    };
    nextButtonStyle.value = {
      top: `${buttonTop}px`,
      right: `${nextRight}px`
    };
  };

  /** 收到书本显示矩形后缓存并重算布局 */
  const onBookDisplaySizeReady = (payload: BookDisplayRect) => {
    if (payload.width <= 0 || payload.height <= 0 || payload.containerWidth <= 0) return;
    lastBookRect = payload;
    applyControlsLayout();
  };

  const pageStatusLabel = computed(
    () => `第 ${currentPageLabel.value} 页，共 ${totalPages.value} 页`
  );
  const volumePercentLabel = computed(() => `${Math.round(effectiveVolume.value * 100)}%`);
  const volumeRangeStyle = computed(() => ({
    '--mobile-volume-percent': `${effectiveVolume.value * 100}%`
  }));
  const playProgressStyle = computed(() => ({
    '--playback-progress': `${Math.max(0, Math.min(1, playbackProgress.value)) * 360}deg`
  }));

  const onVolumeInput = (event: Event) => {
    emit('setVolume', Number((event.target as HTMLInputElement).value));
  };

  /** 同步控件交互态，避免翻页手势与控件操作冲突 */
  const setInteractionActive = (active: boolean) => {
    if (interactionActive === active) return;
    interactionActive = active;
    emit('interactionChange', active);
  };

  /** 关闭工具面板与音量条 */
  const closeTools = () => {
    toolsOpen.value = false;
    volumeOpen.value = false;
    layoutPickerOpen.value = false;
  };

  const toggleVolumePanel = () => {
    volumeOpen.value = !volumeOpen.value;
    if (volumeOpen.value) layoutPickerOpen.value = false;
  };

  const toggleLayoutPicker = () => {
    layoutPickerOpen.value = !layoutPickerOpen.value;
    if (layoutPickerOpen.value) volumeOpen.value = false;
  };

  /** 点击工具区/设置按钮以外区域时关闭工具面板 */
  const onOutsidePointerDown = (event: PointerEvent) => {
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('.mobile-book-controls__tools, .mobile-book-controls__settings')
    ) {
      return;
    }
    closeTools();
  };

  /** 控件按下：标记交互中，阻止事件冒泡到阅读器 */
  const onControlPointerDown = (event: PointerEvent) => {
    event.stopPropagation();
    activeControlPointers.add(event.pointerId);
    setInteractionActive(true);
  };

  /** 控件抬起：无活动指针时解除交互态 */
  const onControlPointerEnd = (event: PointerEvent) => {
    event.stopPropagation();
    activeControlPointers.delete(event.pointerId);
    if (activeControlPointers.size === 0) setInteractionActive(false);
  };

  /** 按滑杆指针位置换算音量 */
  const setVolumeFromPointer = (event: PointerEvent) => {
    const range = volumeRangeRef.value;
    if (!range) return;

    // offsetX 始终基于滑杆自身坐标，阅读器整体旋转时也能正确换算。
    const ratio = event.offsetX / range.clientWidth;
    emit('setVolume', Math.max(0, Math.min(1, ratio)));
  };

  /** 音量滑杆按下开始拖动 */
  const onVolumePointerDown = (event: PointerEvent) => {
    event.preventDefault();
    volumeDragging = true;
    setVolumeFromPointer(event);
    volumeRangeRef.value?.setPointerCapture(event.pointerId);
  };

  /** 音量滑杆拖动中更新音量 */
  const onVolumePointerMove = (event: PointerEvent) => {
    if (!volumeDragging) return;
    event.preventDefault();
    setVolumeFromPointer(event);
  };

  /** 音量滑杆抬起结束拖动 */
  const onVolumePointerUp = (event: PointerEvent) => {
    if (!volumeDragging) return;
    volumeDragging = false;
    if (volumeRangeRef.value?.hasPointerCapture(event.pointerId)) {
      volumeRangeRef.value.releasePointerCapture(event.pointerId);
    }
  };

  watch(
    () => props.isLandscape,
    () => {
      toolsOpen.value = false;
      volumeOpen.value = false;
      layoutPickerOpen.value = false;
    }
  );

  // 布局模式（竖屏单页 ↔ 侧栏）切换时，用缓存矩形立即重算按钮与页码位置
  watch(isBottomLayout, () => {
    applyControlsLayout();
  });

  // 工具面板打开时监听外部点击关闭；关闭时顺带收起音量条
  watch(toolsOpen, (open) => {
    if (open) {
      document.addEventListener('pointerdown', onOutsidePointerDown, true);
      return;
    }

    volumeOpen.value = false;
    layoutPickerOpen.value = false;
    document.removeEventListener('pointerdown', onOutsidePointerDown, true);
  });

  onMounted(() => {
    emitter.on(EVENT_NAMES.BOOK_DISPLAY_SIZE_READY, onBookDisplaySizeReady);
  });

  onBeforeUnmount(() => {
    emitter.off(EVENT_NAMES.BOOK_DISPLAY_SIZE_READY, onBookDisplaySizeReady);
    activeControlPointers.clear();
    setInteractionActive(false);
    document.removeEventListener('pointerdown', onOutsidePointerDown, true);
  });
</script>

<style scoped>
  .mobile-book-controls {
    --mobile-book-top: 15%;
    --mobile-book-height: 70%;
    --mobile-side-rail-width: 60px;
    --mobile-round-button-size: 40px;
    --mobile-safe-right: max(13px, env(safe-area-inset-right));
    --mobile-tools-right: calc(var(--mobile-safe-right) + 109px);
    --mobile-side-button-offset: calc(
      (var(--mobile-side-rail-width) - var(--mobile-round-button-size)) / 2
    );

    position: absolute;
    inset: 0;
    z-index: 12;
    color: #fff;
    pointer-events: none;
    user-select: none;
  }

  .mobile-book-controls__round-button,
  .mobile-book-controls__tools,
  .mobile-book-controls__page {
    pointer-events: auto;
  }

  .mobile-book-controls__round-button {
    position: absolute;
    width: var(--mobile-round-button-size);
    height: var(--mobile-round-button-size);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: #fff;
    background: rgba(44, 44, 44, 0.5);
    border: none;
    border-radius: 50%;
    cursor: pointer;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-book-controls__round-button:focus-visible,
  .mobile-book-controls__tool-button:focus-visible,
  .mobile-book-controls__layout-button:focus-visible,
  .mobile-book-controls__layout-option:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  .mobile-book-controls__round-button:disabled,
  .mobile-book-controls__tool-button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .mobile-book-controls__round-button:active:not(:disabled),
  .mobile-book-controls__tool-button:active:not(:disabled),
  .mobile-book-controls__layout-button:active,
  .mobile-book-controls__layout-option:active {
    transform: scale(0.94);
  }

  .mobile-book-controls__previous,
  .mobile-book-controls__next {
    top: calc(50% - var(--mobile-round-button-size) / 2);
  }

  .mobile-book-controls__previous {
    left: var(--mobile-side-button-offset);
  }

  .mobile-book-controls__next {
    right: var(--mobile-side-button-offset);
  }

  .mobile-book-controls__primary-actions {
    position: absolute;
    top: max(12px, calc(var(--mobile-book-top) - 60px));
    right: max(13px, env(safe-area-inset-right));
    display: flex;
    flex-direction: row-reverse;
    gap: 8px;
    pointer-events: none;
  }

  .mobile-book-controls__primary-actions .mobile-book-controls__round-button {
    position: relative;
    pointer-events: auto;
  }

  .mobile-book-controls__settings img {
    width: 26px;
    height: 26px;
    display: block;
  }

  .mobile-book-controls__play :deep(.book-control-icon) {
    position: relative;
    z-index: 1;
    width: 26px;
    height: 26px;
  }

  .mobile-book-controls__play.is-active::before {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: conic-gradient(#6033e1 var(--playback-progress), #444 0);
    mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 0);
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 0);
    pointer-events: none;
  }

  .mobile-book-controls__tools {
    position: absolute;
    top: max(12px, calc(var(--mobile-book-top) - 60px));
    right: var(--mobile-tools-right);
    width: max-content;
    height: 40px;
    display: block;
    padding: 2px 12px 2px 8px;
    color: #fff;
    background: #2c2c2c;
    border: 1px solid rgba(68, 68, 68, 0.5);
    border-radius: 66px;
    box-sizing: border-box;
    z-index: 2;
    transform-origin: right center;
    overflow: visible;
  }

  .mobile-book-controls__toolbar {
    height: 36px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .mobile-book-controls__tool-button {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: #fff;
    background: transparent;
    border: 0;
    border-radius: 50%;
    cursor: pointer;
    touch-action: manipulation;
  }

  .mobile-book-controls__tool-button.is-active {
    background: rgba(255, 255, 255, 0.08);
  }

  .mobile-book-controls__tool-button--compact {
    width: 32px;
    flex-basis: 32px;
  }

  .mobile-book-controls__tool-button img {
    width: 16px;
    height: 16px;
    display: block;
  }

  .mobile-book-controls__divider {
    width: 1px;
    height: 20px;
    flex: 0 0 1px;
    background: #444;
  }

  .mobile-book-controls__layout-button {
    width: 18px;
    height: 36px;
    flex: 0 0 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: #fff;
    background: transparent;
    border: 0;
    cursor: pointer;
    touch-action: manipulation;
  }

  .mobile-book-controls__layout-icon {
    position: relative;
    width: 16px;
    height: 14px;
    display: block;
    border: 1.3px solid currentColor;
    border-radius: 1.5px;
    box-sizing: border-box;
  }

  .mobile-book-controls__layout-icon span {
    position: absolute;
    right: 2px;
    bottom: 1px;
    left: 2px;
    height: 3.5px;
    background: rgba(255, 255, 255, 0.9);
  }

  .mobile-book-controls__layout-picker {
    position: absolute;
    top: 49px;
    right: calc(var(--mobile-safe-right) - var(--mobile-tools-right));
    width: max-content;
    min-width: 220px;
    height: 185px;
    color: #fff;
    background: #2c2c2c;
    border-radius: 11px;
    box-sizing: border-box;
    overflow: hidden;
    z-index: 3;
    transform-origin: top right;
  }

  .mobile-book-controls__layout-picker-heading {
    height: 33px;
    padding: 12px 14px 4px;
    color: rgba(255, 255, 255, 0.4);
    font-family: Inter, system-ui, sans-serif;
    font-size: 11px;
    font-weight: 600;
    line-height: 17px;
    letter-spacing: 0.55px;
    box-sizing: border-box;
  }

  .mobile-book-controls__layout-options {
    width: max-content;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px;
  }

  .mobile-book-controls__layout-option {
    width: 220px;
    min-width: 204px;
    height: 67px;
    flex: 0 0 67px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    color: #fff;
    text-align: left;
    background: transparent;
    border: 0;
    border-radius: 12px;
    box-sizing: border-box;
    cursor: pointer;
    touch-action: manipulation;
  }

  .mobile-book-controls__layout-option.is-selected {
    background: rgba(255, 255, 255, 0.1);
  }

  .mobile-book-controls__layout-preview {
    position: relative;
    width: 38px;
    height: 28px;
    flex: 0 0 38px;
    display: block;
    background: #2a2a2a;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    box-sizing: border-box;
    overflow: hidden;
  }

  .mobile-book-controls__layout-preview-content {
    position: absolute;
    inset: 1px;
    display: block;
    background: #2a2a2a;
  }

  .mobile-book-controls__layout-preview--excluded .mobile-book-controls__layout-preview-content {
    bottom: 9px;
    background: #111;
  }

  .mobile-book-controls__layout-preview-rail {
    position: absolute;
    right: 1px;
    bottom: 1px;
    left: 1px;
    height: 8px;
    display: block;
    background: rgba(255, 255, 255, 0.5);
  }

  .mobile-book-controls__layout-preview-control {
    position: absolute;
    right: 5px;
    bottom: 5px;
    left: 5px;
    height: 6px;
    display: block;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 2px;
  }

  .mobile-book-controls__layout-copy {
    width: max-content;
    min-width: 136px;
    display: flex;
    flex-direction: column;
    white-space: nowrap;
  }

  .mobile-book-controls__layout-title {
    font-family: Inter, system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 17px;
  }

  .mobile-book-controls__layout-description {
    padding-top: 2px;
    color: rgba(255, 255, 255, 0.45);
    font-family: Inter, system-ui, sans-serif;
    font-size: 11px;
    font-weight: 500;
    line-height: 14px;
  }

  .mobile-layout-picker-enter-active,
  .mobile-layout-picker-leave-active {
    transition:
      opacity 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
      transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  .mobile-layout-picker-enter-from,
  .mobile-layout-picker-leave-to {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
  }

  .mobile-book-controls__volume {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 253px;
    height: 52px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    color: #fff;
    background: #2c2c2c;
    border: 1px solid rgba(68, 68, 68, 0.7);
    border-radius: 14px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
    pointer-events: auto;
    transform-origin: right top;
  }

  .mobile-book-controls__volume-mute {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: #fff;
    background: transparent;
    border: 0;
    border-radius: 50%;
    touch-action: manipulation;
  }

  .mobile-book-controls__volume-range {
    min-width: 0;
    height: 28px;
    flex: 1 1 auto;
    margin: 0;
    appearance: none;
    -webkit-appearance: none;
    background: transparent;
    cursor: pointer;
    touch-action: none;
    pointer-events: auto;
  }

  .mobile-book-controls__volume-range::-webkit-slider-runnable-track {
    height: 4px;
    border-radius: 999px;
    background: linear-gradient(
      90deg,
      #fff var(--mobile-volume-percent),
      #5d5d5d var(--mobile-volume-percent)
    );
  }

  .mobile-book-controls__volume-range::-webkit-slider-thumb {
    width: 18px;
    height: 18px;
    margin-top: -7px;
    appearance: none;
    -webkit-appearance: none;
    background: #fff;
    border: 0;
    border-radius: 50%;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.35);
  }

  .mobile-book-controls__volume-range::-moz-range-track {
    height: 4px;
    border-radius: 999px;
    background: #5d5d5d;
  }

  .mobile-book-controls__volume-range::-moz-range-progress {
    height: 4px;
    border-radius: 999px;
    background: #fff;
  }

  .mobile-book-controls__volume-range::-moz-range-thumb {
    width: 18px;
    height: 18px;
    background: #fff;
    border: 0;
    border-radius: 50%;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.35);
  }

  .mobile-book-controls__volume-value {
    width: 32px;
    flex: 0 0 32px;
    color: rgba(255, 255, 255, 0.84);
    font-family: Inter, system-ui, sans-serif;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  .mobile-book-controls__page {
    position: absolute;
    top: calc(var(--mobile-book-top) + var(--mobile-book-height) + 23px);
    left: 50%;
    min-width: 72px;
    height: 27px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    color: #fff;
    background: rgba(44, 44, 44, 0.5);
    border-radius: 6px;
    box-sizing: border-box;
    font-family: Inter, system-ui, sans-serif;
    font-size: 16px;
    font-variant-numeric: tabular-nums;
    line-height: 19px;
    text-align: center;
    white-space: nowrap;
    transform: translateX(-50%);
  }

  /* 竖屏单页底部布局：页码行高与 40px 翻页按钮同高，实现垂直居中对齐 */
  .mobile-book-controls__page--bottom {
    height: var(--mobile-round-button-size);
  }

  .mobile-book-controls--landscape .mobile-book-controls__primary-actions {
    top: max(13px, env(safe-area-inset-top));
    right: var(--mobile-side-button-offset);
    flex-direction: column;
    gap: 10px;
  }

  .mobile-book-controls--landscape {
    --mobile-tools-right: var(--mobile-side-rail-width);
  }

  .mobile-book-controls--landscape .mobile-book-controls__tools {
    top: max(13px, env(safe-area-inset-top));
  }

  .mobile-book-controls--landscape .mobile-book-controls__volume {
    right: -53px;
    height: 81px;
  }

  .mobile-book-controls--landscape .mobile-book-controls__page {
    top: auto;
    bottom: max(4px, env(safe-area-inset-bottom));
  }

  .mobile-book-tools-enter-active,
  .mobile-book-tools-leave-active {
    transition:
      opacity 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  .mobile-book-tools-enter-from,
  .mobile-book-tools-leave-to {
    opacity: 0;
    transform: translateX(10px) scale(0.96);
  }

  .mobile-volume-enter-active,
  .mobile-volume-leave-active {
    transition:
      opacity 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
      transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  .mobile-volume-enter-from,
  .mobile-volume-leave-to {
    opacity: 0;
    transform: translateY(-6px) scale(0.96);
  }

  @media (prefers-reduced-motion: reduce) {
    .mobile-book-tools-enter-active,
    .mobile-book-tools-leave-active,
    .mobile-volume-enter-active,
    .mobile-volume-leave-active,
    .mobile-layout-picker-enter-active,
    .mobile-layout-picker-leave-active {
      transition: none;
    }
  }
</style>
